import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Mail, 
  MessageSquare, 
  Phone, 
  Save, 
  RefreshCw, 
  AlertCircle,
  CheckCircle,
  Settings2,
  Key,
  Shield,
  Activity,
  Send
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminNotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providersStatus, setProvidersStatus] = useState({});
  const [businessSettings, setBusinessSettings] = useState({
    channels_enabled: { email: true, sms: true, whatsapp: false },
    default_recipients: { email: [], sms: [], whatsapp: [] },
    rate_limits: { email: 100, sms: 50, whatsapp: 30 }
  });
  const [stats, setStats] = useState(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [testChannel, setTestChannel] = useState("email");
  const [sending, setSending] = useState(false);

  // For now, we'll use a default business ID. In production, this would come from auth context
  const businessId = "speakr-admin";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, settingsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/notifications/providers/status`),
        fetch(`${API_URL}/api/v2/notifications/settings/${businessId}`),
        fetch(`${API_URL}/api/v2/notifications/stats/${businessId}`)
      ]);

      if (statusRes.ok) {
        setProvidersStatus(await statusRes.json());
      }
      if (settingsRes.ok) {
        setBusinessSettings(await settingsRes.json());
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error("Failed to load notification settings:", error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/v2/notifications/settings/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channels_enabled: businessSettings.channels_enabled,
          default_recipients: businessSettings.default_recipients,
          rate_limits: businessSettings.rate_limits
        })
      });

      if (res.ok) {
        toast.success("Settings saved successfully");
        loadData();
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTestNotification = async () => {
    if (!testRecipient) {
      toast.error("Please enter a recipient");
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/v2/notifications/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_id: businessId,
          channel: testChannel,
          recipient: testRecipient,
          subject: "Speakr Test Notification",
          body: "This is a test notification from Speakr Enterprise. If you receive this, your notification channel is working correctly."
        })
      });

      const data = await res.json();
      
      if (data.status === "sent") {
        const isMock = providersStatus[testChannel]?.status === "mock";
        toast.success(`Test notification sent${isMock ? " (mock mode)" : ""}!`);
        loadData();
      } else {
        toast.error(`Notification ${data.status}: Check logs for details`);
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    } finally {
      setSending(false);
    }
  };

  const toggleChannel = (channel) => {
    setBusinessSettings(prev => ({
      ...prev,
      channels_enabled: {
        ...prev.channels_enabled,
        [channel]: !prev.channels_enabled[channel]
      }
    }));
  };

  const updateRateLimit = (channel, value) => {
    setBusinessSettings(prev => ({
      ...prev,
      rate_limits: {
        ...prev.rate_limits,
        [channel]: parseInt(value) || 0
      }
    }));
  };

  const getStatusBadge = (providerInfo) => {
    if (!providerInfo) return <Badge variant="outline">Unknown</Badge>;
    
    // Use new status_label if available, fallback to status
    if (providerInfo.is_live) {
      return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Live</Badge>;
    } else if (providerInfo.is_mock) {
      return <Badge variant="secondary"><Activity className="h-3 w-3 mr-1" />Mock Mode</Badge>;
    } else if (providerInfo.status === "error") {
      return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Error</Badge>;
    } else if (providerInfo.status === "disabled") {
      return <Badge variant="outline">Disabled</Badge>;
    }
    return <Badge variant="outline">{providerInfo.status_label || providerInfo.status}</Badge>;
  };
  
  const getStatusDescription = (providerInfo) => {
    if (!providerInfo) return "Status unknown";
    return providerInfo.status_detail || (
      providerInfo.is_live ? "Connected and ready to send" :
      providerInfo.is_mock ? "API keys not configured - notifications logged but not sent" :
      "Provider not configured"
    );
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "email": return <Mail className="h-5 w-5" />;
      case "sms": return <Phone className="h-5 w-5" />;
      case "whatsapp": return <MessageSquare className="h-5 w-5" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Notification Settings</h1>
          <p className="text-gray-500 mt-1">Configure email, SMS, and WhatsApp delivery channels</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={saveSettings} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="providers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="providers">
            <Key className="h-4 w-4 mr-2" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="channels">
            <Settings2 className="h-4 w-4 mr-2" />
            Channels
          </TabsTrigger>
          <TabsTrigger value="test">
            <Send className="h-4 w-4 mr-2" />
            Test
          </TabsTrigger>
          <TabsTrigger value="stats">
            <Activity className="h-4 w-4 mr-2" />
            Statistics
          </TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-600" />
                Provider Status
              </CardTitle>
              <CardDescription>
                Status of notification delivery providers. Configure API keys in environment variables.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {/* Email Provider */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-blue-600" />
                        <CardTitle className="text-base">Email (Resend)</CardTitle>
                      </div>
                      {getStatusBadge(providersStatus.email)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">
                      {getStatusDescription(providersStatus.email)}
                    </p>
                  </CardContent>
                </Card>

                {/* SMS Provider */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="h-5 w-5 text-green-600" />
                        <CardTitle className="text-base">SMS (Twilio)</CardTitle>
                      </div>
                      {getStatusBadge(providersStatus.sms)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">
                      {getStatusDescription(providersStatus.sms)}
                    </p>
                  </CardContent>
                </Card>

                {/* WhatsApp Provider */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-emerald-600" />
                        <CardTitle className="text-base">WhatsApp (Twilio)</CardTitle>
                      </div>
                      {getStatusBadge(providersStatus.whatsapp)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">
                      {getStatusDescription(providersStatus.whatsapp)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Environment Variables Required
                </h4>
                <div className="mt-2 text-sm text-amber-700 space-y-1">
                  <p><code className="bg-amber-100 px-1 rounded">RESEND_API_KEY</code> - Resend API key for email</p>
                  <p><code className="bg-amber-100 px-1 rounded">TWILIO_ACCOUNT_SID</code> - Twilio Account SID</p>
                  <p><code className="bg-amber-100 px-1 rounded">TWILIO_AUTH_TOKEN</code> - Twilio Auth Token</p>
                  <p><code className="bg-amber-100 px-1 rounded">TWILIO_SMS_FROM</code> - Twilio phone number for SMS</p>
                  <p><code className="bg-amber-100 px-1 rounded">TWILIO_WHATSAPP_FROM</code> - WhatsApp sender number</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Channels Tab */}
        <TabsContent value="channels" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Channel Configuration</CardTitle>
              <CardDescription>Enable/disable channels and set rate limits per hour</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {["email", "sms", "whatsapp"].map((channel) => (
                <div key={channel} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    {getChannelIcon(channel)}
                    <div>
                      <h4 className="font-medium capitalize">{channel}</h4>
                      <p className="text-sm text-gray-500">
                        {channel === "email" && "Send notifications via email"}
                        {channel === "sms" && "Send SMS text messages"}
                        {channel === "whatsapp" && "Send WhatsApp messages"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`rate-${channel}`} className="text-sm text-gray-500">
                        Rate limit/hr:
                      </Label>
                      <Input
                        id={`rate-${channel}`}
                        type="number"
                        value={businessSettings.rate_limits[channel]}
                        onChange={(e) => updateRateLimit(channel, e.target.value)}
                        className="w-20"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`toggle-${channel}`}>Enabled</Label>
                      <Switch
                        id={`toggle-${channel}`}
                        checked={businessSettings.channels_enabled[channel]}
                        onCheckedChange={() => toggleChannel(channel)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Tab */}
        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Send Test Notification</CardTitle>
              <CardDescription>
                Test your notification channels by sending a test message
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-4">
                  {["email", "sms", "whatsapp"].map((channel) => (
                    <Button
                      key={channel}
                      variant={testChannel === channel ? "default" : "outline"}
                      onClick={() => setTestChannel(channel)}
                      disabled={!businessSettings.channels_enabled[channel]}
                    >
                      {getChannelIcon(channel)}
                      <span className="ml-2 capitalize">{channel}</span>
                    </Button>
                  ))}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="test-recipient">
                      {testChannel === "email" ? "Email Address" : "Phone Number"}
                    </Label>
                    <Input
                      id="test-recipient"
                      placeholder={testChannel === "email" ? "test@example.com" : "+14165551234"}
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                    />
                  </div>
                </div>

                <Button onClick={sendTestNotification} disabled={sending || !testRecipient}>
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? "Sending..." : "Send Test"}
                </Button>

                {providersStatus[testChannel]?.status === "mock" && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Running in mock mode - notifications will be logged but not actually sent
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="stats" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Delivery Statistics</CardTitle>
              <CardDescription>Notification delivery metrics by channel</CardDescription>
            </CardHeader>
            <CardContent>
              {stats ? (
                <div className="grid gap-4 md:grid-cols-3">
                  {Object.entries(stats.by_channel || {}).map(([channel, data]) => (
                    <Card key={channel}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          {getChannelIcon(channel)}
                          <CardTitle className="text-base capitalize">{channel}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Total</span>
                            <span className="font-medium">{data.total}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Sent</span>
                            <span className="text-green-600">{data.sent}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Delivered</span>
                            <span className="text-blue-600">{data.delivered}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Failed</span>
                            <span className="text-red-600">{data.failed}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Pending/Retrying</span>
                            <span className="text-amber-600">{data.pending + data.retrying}</span>
                          </div>
                          {data.mock > 0 && (
                            <div className="flex justify-between pt-2 border-t">
                              <span className="text-gray-500">Mock mode</span>
                              <span className="text-gray-400">{data.mock}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No statistics available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
